import { cleanSequence } from "./sequence";

// Per-residue monophosphate masses (g/mol) used by the standard oligo MW
// formulas, with a −61.96 adjustment for the terminal phosphate.
const DNA_MONO: Record<string, number> = {
  A: 313.21,
  T: 304.2,
  C: 289.18,
  G: 329.21,
};
const RNA_MONO: Record<string, number> = {
  A: 329.21,
  U: 306.17,
  C: 305.18,
  G: 345.21,
};
const ADJUST = -61.96;

// OD260 conversion factors: 1 A260 unit ≈ this many µg/mL.
export const OD_FACTORS = { dsDNA: 50, ssDNA: 33, ssRNA: 40 } as const;
export type NucleicAcidType = keyof typeof OD_FACTORS;

function counts(
  seq: string,
  rna: boolean,
): { ok: boolean; c: Record<string, number> } {
  const c: Record<string, number> = {};
  const alphabet = rna ? "ACGU" : "ACGT";
  for (const ch of seq) {
    const b = rna && ch === "T" ? "U" : !rna && ch === "U" ? "T" : ch;
    if (!alphabet.includes(b)) return { ok: false, c };
    c[b] = (c[b] ?? 0) + 1;
  }
  return { ok: true, c };
}

export function ssDnaMW(input: string): number | null {
  const seq = cleanSequence(input).replace(/U/g, "T");
  if (seq.length === 0) return null;
  const { ok, c } = counts(seq, false);
  if (!ok) return null;
  return (
    Object.entries(c).reduce((sum, [b, n]) => sum + DNA_MONO[b] * n, 0) + ADJUST
  );
}

export function ssRnaMW(input: string): number | null {
  const seq = cleanSequence(input).replace(/T/g, "U");
  if (seq.length === 0) return null;
  const { ok, c } = counts(seq, true);
  if (!ok) return null;
  return (
    Object.entries(c).reduce((sum, [b, n]) => sum + RNA_MONO[b] * n, 0) + ADJUST
  );
}

/** Double-stranded DNA MW = sense strand + its complement. */
export function dsDnaMW(input: string): number | null {
  const seq = cleanSequence(input).replace(/U/g, "T");
  if (seq.length === 0) return null;
  const { ok, c } = counts(seq, false);
  if (!ok) return null;
  // Complement strand swaps A↔T and G↔C counts.
  const comp: Record<string, number> = {
    A: c.T ?? 0,
    T: c.A ?? 0,
    G: c.C ?? 0,
    C: c.G ?? 0,
  };
  const top =
    Object.entries(c).reduce((s, [b, n]) => s + DNA_MONO[b] * n, 0) + ADJUST;
  const bottom =
    Object.entries(comp).reduce((s, [b, n]) => s + DNA_MONO[b] * n, 0) + ADJUST;
  return top + bottom;
}

export function nucleicAcidLength(input: string): number {
  return cleanSequence(input).length;
}
